import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import { css } from '@emotion/core';
import i18next from 'i18next';

import LayoutInner from 'features/login/components/LayoutInner';
import Panel from 'features/login/components/Panel';
import OtpInputGroup from 'features/login/components/pageOtpVerify/OtpInputGroup';
import OtpCountdownTimer from 'features/login/components/pageOtpVerify/OtpCountdownTimer';
import OtpResendButton from 'features/login/components/pageOtpVerify/OtpResendButton';
import { errMsgWrapperStyle } from 'features/login/styles/comman';
import { setTextVariantStyle } from 'utils/text';
import { useRedirectApp, toSignInPage } from 'features/login/hooks/useInitAndRedirectApp';
import useHistoryWithCurrentSearchHash from 'features/login/hooks/useHistoryWithCurrentSearchHash';
import { signout } from 'features/login/utils/firebaseManager';
import { validateOtpApi } from 'api/otp';
import { error, neutral800, neutral900 } from '@fever/ui';
import { removeBtnDefaultStyle } from 'styles/common';
import { setOtpVerified, setOtpExpired, sendOtp } from 'store/module/firebase';

const PageOtpVerify = () => {
    const dispatch = useDispatch();
    const history = useHistoryWithCurrentSearchHash();
    const redirectApp = useRedirectApp();
    const { currentInputEmail, otpVerification, user } = useSelector(s => s.firebase);
    const { timeStamp, isExpired } = otpVerification;

    // 配置：是否顯示倒數計時器（未來可能啟用）
    const showCountdownTimer = true;

    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    // Test helper: listen for test events to simulate expiration
    useEffect(() => {
        const handleTestExpired = () => {
            console.log('Test helper: Setting OTP to expired state');
            dispatch(setOtpExpired());
        };

        window.addEventListener('test-otp-expired', handleTestExpired);
        return () => window.removeEventListener('test-otp-expired', handleTestExpired);
    }, [dispatch]);

    const handleOtpChange = values => {
        setOtpValues(values);
        setError(''); // Clear error on input change
    };

    const handleOtpComplete = async otp => {
        await handleVerifyOtp(otp);
    };

    const handleVerifyOtp = async (otp = otpValues.join('')) => {
        if (!otp || otp.length !== 6) return;

        try {
            setIsVerifying(true);
            setError('');

            await validateOtpApi(otp);
            // 先更新 Redux 狀態，告訴整個 App OTP 已驗證
            dispatch(setOtpVerified(true));
            redirectApp({ action: 'doLogin' });
        } catch (error) {
            if (error.payload.message === 'OTP validation failed') {
                switch (error.payload.data[0]) {
                    case 'Run out of attempts.':
                        setError(i18next.t('label.verification_failed_exceeded_attempts'));
                        setIsDisabled(true);
                        break;
                    case 'Target not found.':
                        dispatch(setOtpExpired());
                        break;
                    case 'Fail.':
                    default:
                        setError(i18next.t('label.verification_failed_please_retry'));
                        break;
                }
            } else {
                console.error('OTP verification unknown error:', error);
            }
            setOtpValues(['', '', '', '', '', '']);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendOtp = async () => {
        try {
            await dispatch(sendOtp());
            setError('');
            setOtpValues(['', '', '', '', '', '']);
        } catch (error) {
            console.error('Resend OTP error:', error);
            setError(i18next.t('label.resend_failed_please_retry'));
        }
    };

    const handleBackToLogin = async () => {
        // 因為在 OTP 驗證頁，其實 firebase 已經算登入成功，只差一步驗證，所以若返回登入頁，需要強制先登出
        await signout();
        toSignInPage(history);
    };

    useEffect(() => {
        // 只監聽 isVerified 的變化，當 isVerified 從 false 變為 true 時，觸發重導向
        if (otpVerification.isVerified) {
            redirectApp({ action: 'doLogin' });
        } else {
            dispatch(sendOtp());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpVerification.isVerified, dispatch]);

    return (
        <LayoutInner showHeader={false} showMenu={false}>
            <Panel style={panelStyle} data-testid="otp-page">
                <Typography css={titleStyle} variant="h5">
                    {i18next.t('title.login_verification')}
                </Typography>
                <Typography variant="subtitle1" css={descriptionStyle}>
                    {i18next.t('label.verification_code_sent_to')} {currentInputEmail || user.email}
                    <br />
                    {i18next.t('label.enter_within_5_minutes')}
                </Typography>
                {isExpired ? (
                    <OtpResendButton onResend={handleResendOtp} />
                ) : (
                    <>
                        {showCountdownTimer && (
                            <OtpCountdownTimer
                                onExpired={() => {
                                    dispatch(setOtpExpired());
                                    setIsDisabled(false);
                                    setError('');
                                }}
                                initialTime={
                                    timeStamp ? Math.floor((timeStamp - Date.now()) / 1000) : 300
                                }
                            />
                        )}
                        <OtpInputGroup
                            values={otpValues}
                            style={inputGroupStyle}
                            onChange={handleOtpChange}
                            onComplete={handleOtpComplete}
                            error={!!error}
                            isLoading={isVerifying}
                            disabled={isDisabled}
                        />
                        {error && (
                            <div css={[errMsgWrapperStyle, css(`padding: 0px;`)]}>
                                <Typography variant="caption" css={errorTextStyle}>
                                    {error}
                                </Typography>
                            </div>
                        )}
                    </>
                )}
                <div css={backLinkWrapperStyle}>
                    <button css={backLinkStyle} onClick={handleBackToLogin}>
                        <Typography css={backLinkTextStyle}>
                            {i18next.t('btn.back_to_login')}
                        </Typography>
                    </button>
                </div>
            </Panel>
        </LayoutInner>
    );
};

const panelStyle = css`
    background-color: transparent;
    box-shadow: none;

    padding: 32px 24px;

    @media (max-width: 480px) {
        padding: 24px 16px;
    }

    @media (max-width: 360px) {
        padding: 20px 12px;
    }
`;

const titleStyle = css`
    ${setTextVariantStyle('h5')}
    color: ${neutral900};
    text-align: center;
    margin-bottom: 24px;
`;

const descriptionStyle = css`
    ${setTextVariantStyle('subtitle1')}
    color: ${neutral800};
    text-align: center;
    margin-bottom: 16px;
`;

const inputGroupStyle = css`
    margin-bottom: 8px;
`;

const errorTextStyle = css`
    ${setTextVariantStyle('caption')}
    color: ${error};
`;

const backLinkWrapperStyle = css`
    display: flex;
    justify-content: center;
    margin-top: 96px;
`;

const backLinkStyle = css`
    ${removeBtnDefaultStyle}
    text-decoration: none;
    transition: color 0.2s ease;
`;

const backLinkTextStyle = css`
    ${setTextVariantStyle('button')}
    color: rgba(229, 91, 97, 1);
`;

export default PageOtpVerify;
